import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Pagination from "components/pagination";

function ServiceBannersSection({
  desktopBanners,
  mobileBanners,
  handleReplaceBannerClick,
  deleteBanner,
  uploadBanners,
  replaceBannerInputRef,
  replaceBanner,
}) {
  const [selectedType, setSelectedType] = useState("desktop");
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [bannerIndex, setBannerIndex] = useState("");
  const selectedTypeLabel = selectedType === "mobile" ? "Mobile" : "Desktop";
  const selectedDimensions = selectedType === "mobile" ? "420x420" : "1920x300";

  const allRows = useMemo(() => {
    const desktopRows = desktopBanners.map((banner, index) => ({
      ...banner,
      deviceKey: "desktop",
      index,
    }));

    const mobileRows = mobileBanners.map((banner, index) => ({
      ...banner,
      deviceKey: "mobile",
      index,
    }));

    if (activeTab === "desktop") return desktopRows;
    if (activeTab === "mobile") return mobileRows;

    return [...desktopRows, ...mobileRows];
  }, [desktopBanners, mobileBanners, activeTab]);

  const totalPages = Math.max(1, Math.ceil(allRows.length / recordsPerPage));
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRows = allRows.slice(startIndex, startIndex + recordsPerPage);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-6">

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Service Banners</h3>
          <p className="text-sm text-slate-500">Manage desktop and mobile banners for this service.</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
            <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-blue-700">
              Desktop: 1920 x 300 px
            </span>
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
              Mobile: 420 x 420 px
            </span>
          </div>
        </div>

        {/* Add Banner Button */}
        <button
          onClick={() => setShowForm(!showForm)}
          className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700"
        >
          {showForm ? "Close" : "Add Banner"}
        </button>
      </div>

      {/* Upload Form (Only When Clicked) */}
      {showForm && (
        <div className="border-t border-slate-100 pt-6">
          <div className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-slate-50 p-6">
            <div className="grid gap-4 md:grid-cols-2">

              {/* Banner Type */}
              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Banner Type
                </span>
                <select
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                >
                  <option value="desktop">Desktop</option>
                  <option value="mobile">Mobile</option>
                </select>
              </label>

              {/* Index */}
              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Index (Sort Order)
                </span>
                <input
                  type="number"
                  placeholder="Auto if empty"
                  value={bannerIndex}
                  onChange={(e) => setBannerIndex(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>
            </div>

            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Upload requirement for <span className="font-semibold">{selectedTypeLabel}</span>:
              <span className="ml-1 font-semibold">{selectedDimensions} px</span>
            </div>

            {/* File Upload */}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => uploadBanners(e, selectedType, bannerIndex)}
              className="mt-4 w-full rounded-md border border-dashed border-slate-300 bg-white px-3 py-2 text-sm"
            />

            {/* Save Button */}
            <button
              type="button"
              className="mt-4 w-full rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:bg-slate-700"
              onClick={() => {
                setBannerIndex("");
                setShowForm(false);
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {["all", "desktop", "mobile"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
              activeTab === tab
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-700"
            }`}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Table or Empty State */}
      {allRows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-400">
          No banners uploaded yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">

            <thead className="bg-slate-50">
              <tr>
                <th className="p-2 text-left">#</th>
                <th className="p-2 text-left">Preview</th>
                <th className="p-2 text-left">Index</th>
                <th className="p-2 text-left">Type</th>
                <th className="p-2 text-left">Required Size</th>
                <th className="p-2 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedRows.map((row, i) => (
                <tr key={row.id} className="border-t">
                  <td className="p-2">{startIndex + i + 1}</td>
                  <td className="p-2">
                    <img
                      src={row.url}
                      alt=""
                      className="h-12 w-20 object-cover rounded"
                    />
                  </td>
                  <td className="p-2">
                    {row.image_index ?? (startIndex + i + 1)}
                  </td>
                  <td className="p-2 capitalize">{row.deviceKey}</td>
                  <td className="p-2">
                    {row.deviceKey === "mobile" ? "420 x 420 px" : "1920 x 300 px"}
                  </td>
                  <td className="p-2 text-center space-x-2">
                    <button
                      className="text-sky-600"
                      onClick={() => window.open(row.url, "_blank")}
                    >
                      View
                    </button>
                    <button
                      className="text-amber-600"
                      onClick={() =>
                        handleReplaceBannerClick(row.id, row.deviceKey)
                      }
                    >
                      Edit
                    </button>
                    <button
                      className="text-red-600"
                      onClick={() => deleteBanner(row.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

            </tbody>
          </table>
        </div>
      )}

      {allRows.length > 0 && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={setRecordsPerPage}
            totalRecords={allRows.length}
            count={paginatedRows.length}
          showFirstLastControls
          compact
          />
        </div>
      )}

      {/* Hidden Replace Input */}
      <input
        ref={replaceBannerInputRef}
        type="file"
        accept="image/*"
        onChange={replaceBanner}
        className="hidden"
      />

    </div>
  );
}

ServiceBannersSection.propTypes = {
  desktopBanners: PropTypes.array.isRequired,
  mobileBanners: PropTypes.array.isRequired,
  handleReplaceBannerClick: PropTypes.func.isRequired,
  deleteBanner: PropTypes.func.isRequired,
  uploadBanners: PropTypes.func.isRequired,
  replaceBannerInputRef: PropTypes.object.isRequired,
  replaceBanner: PropTypes.func.isRequired,
};

export default ServiceBannersSection;
